<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <Menu/>

          <div class="element-box mt-4">
            <div class="row mt-2">
              <!-- 1. Einzelcoaching -->
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>

              <!-- 2. Gruppenmaßnahmen -->
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  icon="fa-users"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>

              <!-- 3. Soziotherapie -->
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  icon="fa-hand-holding-heart"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>

              <!-- 4. Betreutes Wohnen -->
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  icon="fa-house-chimney-user"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>

              <!-- 5. DRV Fallmanagement -->
              <CoachingButton
                  :enabled="companySettings?.caseManagementCoachingEnabled"
                  route-name="caseManagementCoachingNewAndActiveList"
                  icon="fa-briefcase"
                  coaching-type="caseManagement"
                  label="DRV Fallmanagement"/>

              <!-- 6. Private Weiterbildung -->
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  icon="fa-graduation-cap"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <h6 class="mt-5">{{ $t('general.active_customers') }}</h6>
          <div class="row g-4 mt-1">
            <!-- 1. Einzelcoaching -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.privateCoachingEnabled">
              <CoachingCardInfo
                  label="Einzelcoaching"
                  type="private"
                  :value="infoData?.countActiveCoaching?.counts?.privateCoaching"
              />
            </div>

            <!-- 2. Gruppenmaßnahmen -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.groupCoachingEnabled">
              <CoachingCardInfo
                  label="Gr.-Maßnahmen"
                  type="group"
                  :value="infoData?.countActiveCoaching?.counts?.groupCoaching"
              />
            </div>

            <!-- 3. Soziotherapie -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.socioTherapyCoachingEnabled">
              <CoachingCardInfo
                  label="Soziotherapie"
                  type="socioTherapy"
                  :value="infoData?.countActiveCoaching?.counts?.socioTherapyCoaching"
              />
            </div>

            <!-- 4. Private Weiterbildung -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.privateLearningCoachingEnabled">
              <CoachingCardInfo
                  label="Private Weiterbildung"
                  type="privateLearning"
                  :value="countPrivateLearningCoaching"
              />
            </div>

            <!-- 5. DRV Fallmanagement -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.caseManagementCoachingEnabled">
              <CoachingCardInfo
                  label="DRV Fallmanagement"
                  type="caseManagement"
                  :value="infoData?.countActiveCoaching?.counts?.caseManagementCoaching"
              />
            </div>

            <!-- 6. Betreutes Wohnen -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.assistedLivingCoachingEnabled">
              <CoachingCardInfo
                  label="Betreutes Wohnen"
                  type="assistedLiving"
                  :value="countAssistedLivingCoaching"
              />
            </div>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box pie mt-2">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts
              :key="seriesActiveCoach.length"
              width="100%"
              type="donut"
              :options="chartOptions"
              :series="seriesActiveCoach"/>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import CoachingButton from '@/components/button/CoachingButton.vue';
import CoachingCardInfo from '@/components/card/CoachingCardInfo.vue';
import Menu from '@/views/App/Dashboard/Accountant/Menu.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import {ErrorToast} from '@/components/VueToast';
import {computed, onMounted, ref} from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import {getDonutColorsByCompany, optionsChartDonut} from '@/shared/utils/apex';
import dashboardAccountantService from '@/api/Dashboard/Accountant';
import {piniaUserStore} from '@/store/piniaUserStore';
import {piniaCompanySettingsStore} from "@/store/piniaCompanySettingsStore";

const piniaCompanySettingsState = piniaCompanySettingsStore()
const companySettings = computed(() => piniaCompanySettingsState.companySettings)

const chartOptions = ref(optionsChartDonut);
const seriesActiveCoach = ref([]);
const infoData = ref({});

const countPrivateLearningCoaching = computed(() =>
    (infoData?.value?.countActiveCoaching?.counts?.privateLearningCoaching ?? 0) +
    (infoData?.value?.countNewCoaching?.counts?.privateLearningCoaching ?? 0),
);

const countAssistedLivingCoaching = computed(() =>
    (infoData?.value?.countActiveCoaching?.counts?.assistedLivingCoaching ?? 0) +
    (infoData?.value?.countNewCoaching?.counts?.assistedLivingCoaching ?? 0),
);

async function getInfoData() {
  try {
    const {data} = await dashboardAccountantService.getInfoData();
    infoData.value = data.data;

    const counts = data.data.countActiveCoaching?.counts ?? {};
    const series = [
      counts.privateCoaching ?? 0,
      counts.groupCoaching ?? 0,
      counts.socioTherapyCoaching ?? 0,
      countPrivateLearningCoaching.value,
      countAssistedLivingCoaching.value,
      counts.caseManagementCoaching ?? 0,
    ];
    const countActiveCoachingTotal = series.reduce((sum, value) => sum + value, 0);

    seriesActiveCoach.value = series;
    chartOptions.value = {
      ...chartOptions.value,
      colors: getDonutColorsByCompany(companySettings.value?.companyKey),
      labels: [
        `Einzelcoaching ${series[0]}`,
        `Gruppenmaßnahmen ${series[1]}`,
        `Soziotherapie ${series[2]}`,
        `Private Weiterbildung ${series[3]}`,
        `Betreutes Wohnen ${series[4]}`,
        `DRV Fallmanagement ${series[5]}`,
      ],

      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'left',
        fontSize: '11px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        textColor: '#64748b',
        itemMargin: {
          horizontal: 6,
          vertical: 2
        },
        markers: {
          width: 8,
          height: 8,
          radius: 12,
          offsetX: -3
        }
      },

      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => countActiveCoachingTotal.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(() => {
  getInfoData();
})

</script>

<style scoped>
::v-deep(.pie .apexcharts-legend) {
  display: block !important;
}
</style>
